import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";

const REGIONS = ["서울", "경기", "인천", "부산", "대구", "광주", "대전", "기타"];
const SPORTS = [
  { value: "tennis",       label: "테니스" },
  { value: "soccer",       label: "축구" },
  { value: "futsal",       label: "풋살" },
  { value: "badminton",    label: "배드민턴" },
  { value: "basketball",   label: "농구" },
  { value: "bowling",      label: "볼링" },
  { value: "table_tennis", label: "탁구" },
  { value: "baseball",     label: "야구" },
  { value: "golf",         label: "골프" },
  { value: "pickleball",   label: "피클볼" },
  { value: "billiards",    label: "당구" },
];
const NTRP             = ["2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0+"];
const SOCCER_POS       = ["GK", "DF", "MF", "FW"];
const BADMINTON_LEVELS = ["초심", "D조", "C조", "B조", "A조"];
const BASKETBALL_POS   = ["PG", "SG", "SF", "PF", "C"];
const BASKETBALL_LVLS  = ["하", "중", "상"];
const TABLE_TENNIS_LVL = ["6부", "5부", "4부", "3부", "2부", "1부"];
const BASEBALL_POS     = ["투수", "포수", "내야수", "외야수"];

export default function ProfileSetupPage({ onDone }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [initLoading, setInitLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState(null);

  /* ── 공통 프로필 ── */
  const [profileImage, setProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl]     = useState(null);
  const [nickname, setNickname]         = useState("");
  const [gender, setGender]             = useState(""); // "male" | "female" | ""
  const [region, setRegion]             = useState("");
  const [awards, setAwards]             = useState("");
  const [isProfilePublic, setIsProfilePublic] = useState(false);

  /* ── 종목 프로필 ── */
  const [sportsType, setSportsType]     = useState("");
  const [isPlayer, setIsPlayer]         = useState(false);
  const [careerDetail, setCareerDetail] = useState({});

  /* ── UI ── */
  const [loading, setLoading]   = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /* 기존 데이터 로드 */
  useEffect(() => {
    if (!user?.id) { setInitLoading(false); return; }
    const editSport = localStorage.getItem("profileEditSport");

    Promise.all([
      supabase.from("user_profiles").select("*").eq("id", user.id).maybeSingle(),
      editSport
        ? supabase.from("user_sports").select("*").eq("user_id", user.id).eq("sports_type", editSport).maybeSingle()
        : supabase.from("user_sports").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    ]).then(([{ data: profile }, { data: sport }]) => {
      if (profile) {
        setExistingProfile(profile);
        setNickname(profile.nickname || "");
        setGender(profile.gender || "");
        setRegion(profile.region || "");
        setAwards(profile.awards || "");
        setIsProfilePublic(profile.is_profile_public ?? false);
        if (profile.profile_image) setPreviewUrl(profile.profile_image);
      }
      if (sport) {
        setSportsType(sport.sports_type || "");
        setIsPlayer(sport.is_player ?? false);
        setCareerDetail(sport.career_detail || {});
      }
      localStorage.removeItem("profileEditSport");
      setInitLoading(false);
    });
  }, [user?.id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const updateCareer = (key, value) =>
    setCareerDetail(prev => ({ ...prev, [key]: value }));

  const handleSportsChange = (val) => {
    setSportsType(val);
    setCareerDetail({});
  };

  const handleSave = async () => {
    if (!nickname.trim()) { setErrorMsg("닉네임을 입력해주세요."); return; }
    if (!gender) { setErrorMsg("성별을 선택해주세요."); return; }
    setLoading(true); setErrorMsg("");

    let imageUrl = existingProfile?.profile_image || null;
    if (profileImage) {
      const ext  = profileImage.name.split(".").pop();
      const path = `${user.id}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from("avatars").upload(path, profileImage, { upsert: true });
      if (!uploadErr) {
        const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
        imageUrl = publicUrl;
      }
    }

    const { error: profileErr } = await supabase.from("user_profiles").upsert({
      id: user.id,
      nickname: nickname.trim(),
      gender,
      profile_image: imageUrl,
      region: region || null,
      awards: awards.trim() || null,
      is_profile_public: isProfilePublic,
    });
    if (profileErr) { setLoading(false); setErrorMsg("저장 중 오류가 발생했어요."); return; }

    await supabase.from("profiles").upsert(
      { user_id: user.id, nickname: nickname.trim() },
      { onConflict: "user_id" }
    );

    if (sportsType) {
      const { error: sportErr } = await supabase.from("user_sports").upsert({
        user_id: user.id,
        sports_type: sportsType,
        is_player: isPlayer,
        career_detail: Object.keys(careerDetail).length > 0 ? careerDetail : null,
      }, { onConflict: "user_id,sports_type" });
      if (sportErr) { setLoading(false); setErrorMsg("종목 저장 중 오류가 발생했어요."); return; }
    }

    setLoading(false);
    onDone();
  };

  const renderSportsDetail = () => {
    switch (sportsType) {
      case "tennis":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>NTRP 레이팅</label>
            <div style={s.chipRow}>
              {NTRP.map(v => (
                <button key={v}
                  style={{ ...s.chip, ...(careerDetail.ntrp === v ? s.chipOn : {}) }}
                  onClick={() => updateCareer("ntrp", v)}>{v}</button>
              ))}
            </div>
          </div>
        );
      case "soccer":
      case "futsal":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>포지션</label>
            <div style={s.chipRow}>
              {SOCCER_POS.map(v => (
                <button key={v}
                  style={{ ...s.chip, ...(careerDetail.position === v ? s.chipOn : {}) }}
                  onClick={() => updateCareer("position", v)}>{v}</button>
              ))}
            </div>
          </div>
        );
      case "badminton":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>급수</label>
            <div style={s.chipRow}>
              {BADMINTON_LEVELS.map(v => (
                <button key={v}
                  style={{ ...s.chip, ...(careerDetail.level === v ? s.chipOn : {}) }}
                  onClick={() => updateCareer("level", v)}>{v}</button>
              ))}
            </div>
          </div>
        );
      case "basketball":
        return (
          <>
            <div style={s.fieldWrap}>
              <label style={s.label}>포지션</label>
              <div style={s.chipRow}>
                {BASKETBALL_POS.map(v => (
                  <button key={v}
                    style={{ ...s.chip, ...(careerDetail.position === v ? s.chipOn : {}) }}
                    onClick={() => updateCareer("position", v)}>{v}</button>
                ))}
              </div>
            </div>
            <div style={s.fieldWrap}>
              <label style={s.label}>실력</label>
              <div style={s.chipRow}>
                {BASKETBALL_LVLS.map(v => (
                  <button key={v}
                    style={{ ...s.chip, ...(careerDetail.level === v ? s.chipOn : {}) }}
                    onClick={() => updateCareer("level", v)}>{v}</button>
                ))}
              </div>
            </div>
          </>
        );
      case "bowling":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>평균 점수 (AVG)</label>
            <input style={s.input} type="number" placeholder="예) 150"
              value={careerDetail.avg || ""}
              onChange={e => updateCareer("avg", e.target.value)} />
          </div>
        );
      case "table_tennis":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>부수</label>
            <div style={s.chipRow}>
              {TABLE_TENNIS_LVL.map(v => (
                <button key={v}
                  style={{ ...s.chip, ...(careerDetail.level === v ? s.chipOn : {}) }}
                  onClick={() => updateCareer("level", v)}>{v}</button>
              ))}
            </div>
          </div>
        );
      case "baseball":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>포지션</label>
            <div style={s.chipRow}>
              {BASEBALL_POS.map(v => (
                <button key={v}
                  style={{ ...s.chip, ...(careerDetail.position === v ? s.chipOn : {}) }}
                  onClick={() => updateCareer("position", v)}>{v}</button>
              ))}
            </div>
          </div>
        );
      case "golf":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>핸디캡</label>
            <input style={s.input} type="number" placeholder="예) 18"
              value={careerDetail.handicap || ""}
              onChange={e => updateCareer("handicap", e.target.value)} />
          </div>
        );
      case "pickleball":
      case "billiards":
        return (
          <div style={s.fieldWrap}>
            <label style={s.label}>구력 (연수)</label>
            <input style={s.input} type="number" placeholder="예) 3"
              value={careerDetail.years || ""}
              onChange={e => updateCareer("years", e.target.value)} />
          </div>
        );
      default:
        return null;
    }
  };

  const initials = nickname.trim()
    ? nickname.trim()[0].toUpperCase()
    : (user?.email?.[0] || "?").toUpperCase();

  if (initLoading) return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F5F5F7" }}>
      <style>{`@keyframes pSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "pSpin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>프로필을 설정해주세요</h2>
        <p style={s.subtitle}>팀원들에게 나를 소개해보세요</p>
      </div>

      <div style={s.body}>
        {/* 프로필 사진 */}
        <div style={s.avatarWrap}>
          <div style={s.avatarCircle} onClick={() => fileInputRef.current?.click()}>
            {previewUrl
              ? <img src={previewUrl} alt="profile" style={s.avatarImg} />
              : <span style={s.avatarInitials}>{initials}</span>}
            <div style={s.cameraBadge}>📷</div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*"
            style={{ display: "none" }} onChange={handleImageChange} />
          <p style={s.avatarHint}>사진 선택 (선택사항)</p>
        </div>

        {errorMsg && <div style={s.errorBox}>{errorMsg}</div>}

        {/* 닉네임 */}
        <div style={s.fieldWrap}>
          <label style={s.label}>닉네임 <span style={{ color: "#E8412A" }}>*</span></label>
          <input
            style={s.input}
            type="text"
            placeholder="닉네임을 입력하세요"
            value={nickname}
            onChange={e => { setNickname(e.target.value); setErrorMsg(""); }}
          />
          <p style={s.fieldHint}>실명 또는 이름을 권장해요 (팀 활동에 도움이 돼요)</p>
        </div>

        {/* 성별 */}
        <div style={s.fieldWrap}>
          <label style={s.label}>성별 <span style={{ color: "#E8412A" }}>*</span></label>
          <div style={s.toggleRow}>
            {[{ v: "male", label: "남성" }, { v: "female", label: "여성" }].map(({ v, label }) => (
              <button key={v}
                style={{ ...s.toggleBtn, ...(gender === v ? s.toggleOn : {}) }}
                onClick={() => { setGender(v); setErrorMsg(""); }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 활동 지역 */}
        <div style={s.fieldWrap}>
          <label style={s.label}>활동 지역</label>
          <select style={s.select} value={region} onChange={e => setRegion(e.target.value)}>
            <option value="">지역 선택</option>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* 수상 이력 */}
        <div style={s.fieldWrap}>
          <label style={s.label}>수상 이력 (선택)</label>
          <input style={s.input} type="text"
            placeholder="예) 2023 서울시장배 3위"
            value={awards} onChange={e => setAwards(e.target.value)} />
        </div>

        {/* 주 종목 */}
        <div style={s.fieldWrap}>
          <label style={s.label}>주 종목</label>
          <select style={s.select} value={sportsType}
            onChange={e => handleSportsChange(e.target.value)}>
            <option value="">종목 선택 (선택사항)</option>
            {SPORTS.map(sp => <option key={sp.value} value={sp.value}>{sp.label}</option>)}
          </select>
        </div>

        {/* 종목별 상세 */}
        {renderSportsDetail()}

        {/* 선수 출신 */}
        {sportsType && (
          <div style={s.fieldWrap}>
            <label style={s.label}>선수 출신 여부</label>
            <div style={s.toggleRow}>
              {[{ v: false, label: "아니오" }, { v: true, label: "예" }].map(({ v, label }) => (
                <button key={label}
                  style={{ ...s.toggleBtn, ...(isPlayer === v ? s.toggleOn : {}) }}
                  onClick={() => setIsPlayer(v)}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* 팀원 프로필 공개 동의 */}
        <div style={{ ...s.fieldWrap, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={s.label}>팀원 프로필 공개 동의</span>
              <span style={{ color: "#E8412A", fontSize: 12, fontWeight: 700, marginLeft: 6 }}>사용 추천</span>
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.5 }}>
              교류전 상대팀과 팀 정보 보기에서 내 프로필(닉네임, 종목 레벨)이 공개됩니다
            </div>
          </div>
          <button
            onClick={() => setIsProfilePublic(v => !v)}
            style={{
              width: 48, height: 28, borderRadius: 14, border: "none", cursor: "pointer",
              background: isProfilePublic ? "#E8412A" : "#d1d5db",
              position: "relative", flexShrink: 0, transition: "background 0.2s",
            }}
          >
            <span style={{
              position: "absolute", top: 3,
              left: isProfilePublic ? 23 : 3,
              width: 22, height: 22, borderRadius: "50%", background: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              transition: "left 0.2s",
            }} />
          </button>
        </div>

        <button
          style={{ ...s.primaryBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSave}
          disabled={loading}>
          {loading ? "저장 중..." : "프로필 저장하기"}
        </button>
        <button style={s.skipBtn} onClick={onDone}>나중에 하기</button>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100dvh", background: "#F5F5F7",
    fontFamily: "'Noto Sans KR', sans-serif",
    maxWidth: 480, margin: "0 auto",
  },
  header: {
    background: "#1A1A2E", textAlign: "center",
    padding: "calc(env(safe-area-inset-top) + 40px) 24px 32px",
  },
  title: {
    fontSize: 22, fontWeight: 800, color: "#fff", margin: "0 0 8px",
  },
  subtitle: {
    fontSize: 14, color: "rgba(255,255,255,0.6)", margin: 0,
  },
  body: {
    padding: "28px 20px 80px",
  },
  avatarWrap: {
    display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32,
  },
  avatarCircle: {
    width: 84, height: 84, borderRadius: "50%",
    background: "#E8412A", overflow: "hidden",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", position: "relative",
    boxShadow: "0 4px 16px rgba(232,65,42,0.3)",
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  avatarInitials: { fontSize: 30, fontWeight: 800, color: "#fff" },
  cameraBadge: {
    position: "absolute", bottom: 2, right: 2,
    background: "rgba(0,0,0,0.55)", borderRadius: "50%",
    width: 26, height: 26,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
  },
  avatarHint: { fontSize: 12, color: "#9ca3af", margin: "10px 0 0" },
  errorBox: {
    background: "#fff0f0", border: "1px solid #fecaca",
    borderRadius: 10, padding: "12px 14px",
    fontSize: 13, color: "#dc2626", marginBottom: 20,
  },
  fieldWrap: { marginBottom: 22 },
  label: { display: "block", fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 },
  fieldHint: { fontSize: 12, color: "#9ca3af", margin: "6px 0 0" },
  input: {
    width: "100%", padding: "14px",
    background: "#fff", border: "1.5px solid #e5e7eb",
    borderRadius: 10, color: "#111827", fontSize: 15,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  select: {
    width: "100%", padding: "14px",
    background: "#fff", border: "1.5px solid #e5e7eb",
    borderRadius: 10, color: "#111827", fontSize: 15,
    outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", appearance: "none",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
  },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: {
    padding: "10px 18px",
    background: "#fff", border: "1.5px solid #e5e7eb",
    borderRadius: 20, fontSize: 14, fontWeight: 500,
    color: "#374151", cursor: "pointer", fontFamily: "inherit",
  },
  chipOn: { background: "#E8412A", borderColor: "#E8412A", color: "#fff", fontWeight: 700 },
  toggleRow: { display: "flex", gap: 8 },
  toggleBtn: {
    flex: 1, padding: "14px 0",
    background: "#fff", border: "1.5px solid #e5e7eb",
    borderRadius: 10, fontSize: 14, fontWeight: 500,
    color: "#9ca3af", cursor: "pointer", fontFamily: "inherit",
  },
  toggleOn: { background: "#1A1A2E", borderColor: "#1A1A2E", color: "#fff", fontWeight: 700 },
  primaryBtn: {
    width: "100%", padding: "15px",
    background: "#E8412A", color: "#fff",
    border: "none", borderRadius: 10,
    fontSize: 16, fontWeight: 700,
    cursor: "pointer", marginTop: 8, marginBottom: 12, fontFamily: "inherit",
  },
  skipBtn: {
    width: "100%", padding: "14px",
    background: "#fff", color: "#9ca3af",
    border: "1.5px solid #e5e7eb", borderRadius: 10,
    fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
  },
};
